import { NotificationSettingsDialog } from '@strata/react-dialog';
import { SuccessToast } from '@strata/react-toast-message';
import { type ComponentProps, useEffect, useMemo } from 'react';

import { PayerRequestSummarySettings } from 'components/header/dialogs/notificationSettings/PayerRequestSummarySettings';
import { NotificationTypes } from 'components/notifications/NotificationComponentMap';
import { useAppSelector } from 'store';
import {
    useGetNotificationPreferencesQuery,
    usePostUpdateNotificationPreferencesMutation,
} from 'store/api/notificationApi';

const PayerRequestStatusDisplayNameMap = {
    [NotificationTypes.PR_IN_PROG]: 'In Progress',
    [NotificationTypes.PR_APP_REQ]: 'Approval Requested',
    [NotificationTypes.PR_APPROVE]: 'Approved',
    [NotificationTypes.PR_APP_DEN]: 'Approval Denied',
    [NotificationTypes.PR_RESOLVE]: 'Monitoring',
    [NotificationTypes.PR_CLOSED]: 'Closed',
    [NotificationTypes.PR_CANCEL]: 'Cancelled',
} as const;

const summaryEmailTypes = (
    permissions: string[],
): ComponentProps<typeof NotificationSettingsDialog>['summaryEmailTypes'] => [
    {
        value: NotificationTypes.PAYREQSUM_APERTURE,
        text: 'Daily Request Summary',
        description: 'Summary of open and closed requests delivered in the morning.',
        AdditionalSettings: <PayerRequestSummarySettings />,
        additionalSettingsCallback: (formik, values) =>
            formik.setFieldValue(
                'payerRequestSummaryTypes',
                values.includes(NotificationTypes.PAYREQSUM_APERTURE)
                    ? PayerRequestSummarySettings.Options(permissions).map(({ value }) => value)
                    : [],
            ),
    },
];

const notificationTypes: ComponentProps<typeof NotificationSettingsDialog>['notificationTypes'] = [
    {
        value: NotificationTypes.PAYREQ_APERTURE,
        text: 'A new request is submitted',
    },
    {
        value: NotificationTypes.PAYREQASSIGN_APERTURE,
        text: 'I have been assigned to a request',
    },
    {
        value: 'PR_STATUS',
        text: 'A request that I have been assigned changed status to...',
        subTypes: Object.entries(PayerRequestStatusDisplayNameMap).map(([value, text]) => ({
            value,
            text,
        })),
    },
    {
        value: NotificationTypes.PR_MSG_AP,
        text: 'There’s a new message for a request to which I am assigned',
    },
];

interface UserNotificationSettingsDialogProps {
    isOpen: boolean;
    onSecondaryButtonClick: () => void;
}

export const UserNotificationSettingsDialog = ({
    isOpen,
    onSecondaryButtonClick,
}: UserNotificationSettingsDialogProps) => {
    const { permissions } = useAppSelector((state) => state.auth);

    const [postUpdateNotificationPreferences] = usePostUpdateNotificationPreferencesMutation();
    const { data, isFetching, isError } = useGetNotificationPreferencesQuery(undefined, {
        skip: !isOpen,
    });
    useEffect(() => {
        if (isError) {
            onSecondaryButtonClick();
        }
    }, [isError, onSecondaryButtonClick]);

    const preferences = useMemo(
        () =>
            !isFetching && data
                ? {
                      email: data.email,
                      inApp: data.in_app,
                      ...PayerRequestSummarySettings.initialValues(data),
                  }
                : undefined,
        [data, isFetching],
    );

    return (
        <NotificationSettingsDialog
            isOpen={isOpen}
            notificationTypes={notificationTypes}
            summaryEmailTypes={summaryEmailTypes(permissions)}
            values={preferences}
            onSecondaryButtonClick={onSecondaryButtonClick}
            onSubmit={async (values) => {
                await postUpdateNotificationPreferences({
                    email: values.email,
                    in_app: values.inApp,
                    ...PayerRequestSummarySettings.submitValues(values),
                }).unwrap();
                SuccessToast({ message: 'Your notification settings were updated successfully.' });
                onSecondaryButtonClick();
            }}
            width={770}
        />
    );
};
